// Last-known-status cache for one "batch" (whatever unit of work a feature runs in the
// background), keyed by an id, so a finished — or long-gone — run can still be shown to the user
// after the server's own record of it has expired.
//
// `createBatchSnapshotStore(config)` builds one store for one key prefix. A feature that needs
// its own batch cache (its own prefix, version, and pruning limits) calls this once and gets
// back `{ save, read, prune }`.

/**
 * @param {Object} config
 * @param {string} config.keyPrefix - every snapshot is saved under `${keyPrefix}${id}`; pruning
 *   only ever looks at (and only ever removes) keys under this prefix.
 * @param {number} config.version - stamped into every saved envelope; a stored envelope with a
 *   different version is treated as unreadable.
 * @param {number} config.maxAgeMs - snapshots older than this are dropped when pruning.
 * @param {number} config.maxCount - once age-based pruning is done, only this many of the most
 *   recently saved snapshots are kept.
 */
export function createBatchSnapshotStore({ keyPrefix, version, maxAgeMs, maxCount }) {
  function snapshotKey(id) {
    return `${keyPrefix}${id}`;
  }

  // Saves the latest known status for one batch and returns the envelope that was written (or
  // null if storage refused the write). The payload is whatever the caller wants remembered —
  // this store only adds the version/savedAt stamp around it.
  function save(id, payload) {
    const envelope = { version, savedAt: Date.now(), ...payload };
    try {
      localStorage.setItem(snapshotKey(id), JSON.stringify(envelope));
      return envelope;
    } catch (e) {
      return null;
    }
  }

  function read(id) {
    try {
      const raw = localStorage.getItem(snapshotKey(id));
      if (!raw) return null;

      const envelope = JSON.parse(raw);
      if (!envelope || envelope.version !== version) return null;

      return envelope;
    } catch (e) {
      return null;
    }
  }

  // Keeps the snapshot cache from growing forever: drops anything older than maxAgeMs, then
  // keeps only the maxCount most recently saved snapshots overall. Only ever looks at — and only
  // ever removes — keys under this store's own prefix.
  function prune() {
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(keyPrefix)) {
          keys.push(key);
        }
      }

      const now = Date.now();
      const survivors = [];
      keys.forEach((key) => {
        let savedAt = 0;
        try {
          savedAt = JSON.parse(localStorage.getItem(key))?.savedAt || 0;
        } catch (e) {
          savedAt = 0;
        }

        if (now - savedAt > maxAgeMs) {
          localStorage.removeItem(key);
        } else {
          survivors.push({ key, savedAt });
        }
      });

      survivors
        .sort((a, b) => b.savedAt - a.savedAt)
        .slice(maxCount)
        .forEach(({ key }) => localStorage.removeItem(key));
    } catch (e) {
      // Best-effort cleanup — a broken store just means the cache stays as-is.
    }
  }

  return { save, read, prune };
}
